import { format, isValid } from "date-fns";
import { groupBy, isArray, isEmpty, isString } from "lodash";
import { useMemo } from "react";
import {
  convertToEasternTime,
  normalizeIsoStringToUtc,
} from "../../_src_shared/utils/functions/formatDateTime";

export const useCommentRendererUtilsHook = ({ comments, sort }) => {
  const groupedComments = useMemo(() => {
    if (!isArray(comments) || isEmpty(comments)) {
      return {};
    }

    return groupBy(comments, (comment) => {
      const dateStr = comment?.commentDateTime;
      if (!dateStr) return "Unknown Date";

      const date = isoToEasternDate(dateStr);
      if (!isValid(date)) return "Unknown Date";

      return format(date, "yyyy-MM-dd");
    });
  }, [comments]);

  const dateKeyToDateMap = useMemo(() => {
    const map = {};

    Object.keys(groupedComments).forEach((dateKey) => {
      if (dateKey === "Unknown Date") {
        map[dateKey] = null;
      } else {
        const firstComment = groupedComments[dateKey][0];
        const originalDate = isoToEasternDate(firstComment?.commentDateTime);
        map[dateKey] = originalDate;
      }
    });

    return map;
  }, [groupedComments]);

  const sortedDateKeys = useMemo(() => {
    return Object.keys(groupedComments).sort((a, b) => {
      if (a === "Unknown Date") return 1;
      if (b === "Unknown Date") return -1;

      if (sort === "desc") {
        return b.localeCompare(a);
      } else {
        return a.localeCompare(b);
      }
    });
  }, [groupedComments, sort]);

  const sortedGroupedComments = useMemo(() => {
    const sorted = {};

    Object.keys(groupedComments).forEach((dateKey) => {
      const commentsInGroup = groupedComments[dateKey];

      sorted[dateKey] = [...commentsInGroup].sort((a, b) => {
        const dateA = isoToEasternDate(a?.commentDateTime);
        const dateB = isoToEasternDate(b?.commentDateTime);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        if (sort === "desc") {
          return dateB.getTime() - dateA.getTime();
        } else {
          return dateA.getTime() - dateB.getTime();
        }
      });
    });

    return sorted;
  }, [groupedComments, sort]);

  return { sortedDateKeys, dateKeyToDateMap, sortedGroupedComments };
};

/**
 * Normalizes an ISO string to UTC, then converts to an Eastern Time Date object.
 * This ensures grouping/sorting happens in Eastern Time, not local browser time.
 */
const isoToEasternDate = (isoString) => {
  if (!isoString || !isString(isoString)) return null;

  try {
    const normalized = normalizeIsoStringToUtc(isoString);
    const easternDate = convertToEasternTime(normalized);
    return easternDate;
  } catch (err) {
    console.warn("Invalid ISO string format:", isoString);
    return null;
  }
};
